import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/client';

const EMPTY_FORM = { key: '', name: '', resourceType: '' };

export default function DefinitionsPage() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/bp/definitions');
      setItems(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function patch(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post('/bp/definitions', {
        key:          form.key,
        name:         form.name,
        resourceType: form.resourceType,
      });
      setForm(EMPTY_FORM);
      setAddOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="bp-definitions-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Process Definitions</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ marginBottom: '1rem' }}>
        <button data-testid="bp-definition-add" onClick={() => setAddOpen(true)}>
          Create Definition
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Name</th>
              <th>Resource Type</th>
              <th>Status</th>
              <th>Version</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr
                key={d.id}
                data-testid={`bp-definition-row-${d.id}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/bp/definitions/${d.id}`)}
              >
                <td>{d.key}</td>
                <td>{d.name}</td>
                <td>{d.resource_type}</td>
                <td>{d.status}</td>
                <td>{d.version}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5}>No process definitions yet.</td></tr>}
          </tbody>
        </table>
      )}

      {addOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 380 }}>
            <h3>Create Process Definition</h3>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  placeholder="Key (lowercase, e.g. expense_approval)"
                  value={form.key}
                  onChange={(e) => patch('key', e.target.value)}
                  required
                />
                <input
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => patch('name', e.target.value)}
                  required
                />
                <input
                  placeholder="Resource Type (e.g. expense_report)"
                  value={form.resourceType}
                  onChange={(e) => patch('resourceType', e.target.value)}
                  required
                />
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="bp-definition-form-submit" disabled={saving}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
                <button type="button" onClick={() => { setAddOpen(false); setError(null); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
